import { Checkbox, MultiLocationSuggestOption, useRecentSearches } from '@property.com.au/ui-kit';
import { push } from '@rea-argonaut/link';
import { FunctionComponent, useState } from 'react';
import { arrayToMultiLocationSuggestOptions } from '../../../app-kit/helpers/arrayToMultiLocationSuggestOptions';
import { createSearchUrl } from '../../../helpers/createSearchUrl';
import { SEARCH_LOCATION_DEFAULT, SEARCH_SEPARATOR } from '../../../helpers/defaults';
import { isPropertyStatusAllProperties } from '../../../helpers/isPropertyStatusAllProperties';
import { isPropertyStatusOffMarket } from '../../../helpers/isPropertyStatusOffMarket';
import {
  getBoundingBoxFromUrl,
  getLocationsFromUrl,
  getPolygonsFromUrl,
  getPropertyStatusFromUrl,
  getSearchFlowFromUrl,
  getSurroundingSuburbsUrl,
} from '../../../helpers/queryStringHelpers/queryStringHelpers';
import { isBoundingBoxNotDefault } from '../../../helpers/searchSlugDefaultChecks';
import { MODAL_MAX_WIDTH, MODAL_PADDING, SearchModal } from '../../SearchModal/SearchModal';
import {
  CommuteSearchCTAButton,
  CommuteSearchCTAButtonProps,
} from './subcomponents/CommuteSearchCTAButton';
import { LocationScreenMultiLocationSuggest } from './subcomponents/LocationScreenMultiLocationSuggest';
import { SelectWithFixedPositionOptions } from './subcomponents/SelectWithFixedPositionOptions';

export const MAP_AREA = 'Map area';
export const DRAWN_AREA = 'Drawn area';
const SELECT_WIDTH = `calc(${MODAL_MAX_WIDTH} - ${MODAL_PADDING})`;
const SELECT_MAX_WIDTH = `calc(100vw - ${MODAL_PADDING})`;

interface CommuteAndLocationModalLocationScreenContentProps {
  commuteSearchCTAButton: CommuteSearchCTAButtonProps;
  locationState: MultiLocationSuggestOption[];
  setLocationState: React.Dispatch<React.SetStateAction<MultiLocationSuggestOption[]>>;
  surroundingSuburbsState: boolean;
  setSurroundingSuburbsState: (state: boolean) => void;
  hideSurroundingSuburbsCheckbox?: boolean;
}

export const CommuteAndLocationModalLocationScreenContent: FunctionComponent<
  CommuteAndLocationModalLocationScreenContentProps
> = ({
  commuteSearchCTAButton,
  locationState,
  setLocationState,
  surroundingSuburbsState,
  setSurroundingSuburbsState,
  hideSurroundingSuburbsCheckbox = false,
}) => {
  return (
    <>
      <SelectWithFixedPositionOptions width={SELECT_WIDTH} maxWidth={SELECT_MAX_WIDTH}>
        <LocationScreenMultiLocationSuggest
          locationState={locationState}
          setLocationState={setLocationState}
        />
        {!hideSurroundingSuburbsCheckbox && (
          <Checkbox
            label="Include surrounding suburbs"
            checked={surroundingSuburbsState}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSurroundingSuburbsState(event.target.checked)
            }
            id="surrounding-suburbs-checkbox"
          />
        )}
      </SelectWithFixedPositionOptions>
      <CommuteSearchCTAButton {...commuteSearchCTAButton} />
    </>
  );
};

interface CommuteAndLocationModalLocationScreenProps {
  onClose: () => void;
  searchQuery: string;
  title: string;
  subtitle: string;
  commuteSearchCTAButton: CommuteSearchCTAButtonProps;
  saveRecentLocationSearch: ReturnType<typeof useRecentSearches>['saveRecentLocationSearch'];
}

export const CommuteAndLocationModalLocationScreen: FunctionComponent<
  CommuteAndLocationModalLocationScreenProps
> = ({
  title,
  subtitle,
  onClose,
  searchQuery,
  commuteSearchCTAButton,
  saveRecentLocationSearch,
}) => {
  const surroundingSuburbsFromUrl = getSurroundingSuburbsUrl(searchQuery);
  const [surroundingSuburbs, setSurroundingSuburbs] = useState<boolean>(
    surroundingSuburbsFromUrl !== 'false',
  );

  const flow = getSearchFlowFromUrl(searchQuery);

  const boundingBox = getBoundingBoxFromUrl(searchQuery);
  const hasBoundingBox = isBoundingBoxNotDefault(boundingBox);

  const polygons = getPolygonsFromUrl(searchQuery);

  const locations = getLocationsFromUrl(searchQuery);

  let locationSearchInitialState: MultiLocationSuggestOption[];
  if (polygons) {
    locationSearchInitialState = arrayToMultiLocationSuggestOptions([DRAWN_AREA]);
  } else if (hasBoundingBox) {
    locationSearchInitialState = arrayToMultiLocationSuggestOptions([MAP_AREA]);
  } else {
    locationSearchInitialState = arrayToMultiLocationSuggestOptions(
      locations?.split(SEARCH_SEPARATOR) ?? [],
    );
  }

  const initialLocationState = flow === 'location' ? locationSearchInitialState : [];

  const [locationState, setLocationState] =
    useState<MultiLocationSuggestOption[]>(initialLocationState);

  const onConfirm = () => {
    const location =
      locationState
        .map((selection) => selection?.display?.text)
        .filter(Boolean)
        .filter((text) => text !== SEARCH_LOCATION_DEFAULT)
        .join(SEARCH_SEPARATOR) || SEARCH_LOCATION_DEFAULT;

    createSearchUrl({
      currentUrl: searchQuery,
      updates: {
        flow: 'location',
        commutes: undefined,
        locations: location === MAP_AREA || location === DRAWN_AREA ? undefined : location,
        boundingBox: location === MAP_AREA ? boundingBox : undefined,
        polygons: location === DRAWN_AREA ? polygons : undefined,
        surroundingSuburbs: surroundingSuburbs ? 'true' : 'false',
        pageNumber: '',
      },
      historyPushFn: push,
      saveRecentLocationSearch,
    });

    onClose();
  };

  const propertyStatus = getPropertyStatusFromUrl(searchQuery);

  const shouldHideSurroundingSuburbsCheckbox =
    isPropertyStatusOffMarket(propertyStatus) || isPropertyStatusAllProperties(propertyStatus);

  return (
    <SearchModal
      opened
      title={title}
      subtitle={subtitle}
      onCancel={onClose}
      onConfirm={onConfirm}
      showDialogBodyOverlay={false}
      disableConfirm={locationState.length === 0}
    >
      <CommuteAndLocationModalLocationScreenContent
        commuteSearchCTAButton={commuteSearchCTAButton}
        locationState={locationState}
        setLocationState={setLocationState}
        surroundingSuburbsState={surroundingSuburbs}
        setSurroundingSuburbsState={setSurroundingSuburbs}
        hideSurroundingSuburbsCheckbox={shouldHideSurroundingSuburbsCheckbox}
      />
    </SearchModal>
  );
};
